import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/esm/Add';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import QrCodeScanner from '@mui/icons-material/esm/QrCodeScanner';
import Refresh from '@mui/icons-material/esm/Refresh';
import Reply from '@mui/icons-material/esm/Reply';
import Send from '@mui/icons-material/esm/Send';
import Visibility from '@mui/icons-material/esm/Visibility';
import { PageHeader } from '../../components/layout';
import BarcodeScanner from '../../components/field/BarcodeScanner';
import { RootState } from '../../store';
import { normalizeRole } from '../../routes/fieldAccess';
import teamService, { TeamListItem } from '../../services/api/teamService';
import timesheetService, { Timesheet, TimesheetEntryPayload, TimesheetHourType, TimesheetPayload, TimesheetStatus, Worker } from '../../services/api/timesheetService';
import { resolveWorkerByBadge } from '../../services/offline/workerBadgeLookup';
import { cachedRecords, cachedTeams, cachedWorkers, enqueueOfflineTimesheet, mergeById } from '../../services/offline/fieldOfflineForms';
import TaskPhotoEvidencePanel from './TaskPhotoEvidencePanel';

interface TimesheetsPageProps {
  fieldMode?: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);
const labelize = (value: string) => value.replace(/_/g, ' ');

const newEntry = (): TimesheetEntryPayload => ({
  workerId: '',
  taskId: '',
  hours: 0,
  hourType: 'regular',
  notes: '',
});

export default function TimesheetsPage({ fieldMode = false }: TimesheetsPageProps) {
  const { activeProjectId } = useSelector((state: RootState) => state.projects);
  const user = useSelector((state: RootState) => state.auth.user);
  const role = normalizeRole(user?.role);
  const workerLocked = fieldMode && ['construction', 'worker', 'construction_worker'].includes(role);
  const allowedTeamIds = useMemo(
    () => (workerLocked ? (user?.teamIds ?? []).filter(Boolean) : []),
    [user?.teamIds, workerLocked]
  );
  const activeCrewId = allowedTeamIds[0] ?? '';
  const canApprove = role === 'admin' || role === 'pm';
  const canManageWorkers = fieldMode && (role === 'admin' || role === 'pm');
  const canCreateTimesheet = fieldMode && !workerLocked;
  const [rows, setRows] = useState<Timesheet[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [status, setStatus] = useState<TimesheetStatus | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [workerDialogOpen, setWorkerDialogOpen] = useState(false);
  const [scannerEntryIndex, setScannerEntryIndex] = useState<number | null>(null);
  const [selected, setSelected] = useState<Timesheet | null>(null);
  const [workerForm, setWorkerForm] = useState({ name: '', badgeCode: '', teamId: '', trade: '' });
  const [form, setForm] = useState<TimesheetPayload>({
    projectId: activeProjectId ?? '',
    workDate: today(),
    teamId: '',
    entries: [newEntry()],
  });
  const filterTeamsForCrew = useCallback(
    (teamRows: TeamListItem[]) => teamRows.filter((team) => team.isActive !== false && (!workerLocked || allowedTeamIds.includes(team.id))),
    [allowedTeamIds, workerLocked]
  );
  const filterWorkersForCrew = useCallback(
    (workerRows: Worker[]) => workerRows.filter((worker) => worker.isActive !== false && (!workerLocked || allowedTeamIds.includes(worker.teamId ?? ''))),
    [allowedTeamIds, workerLocked]
  );
  const filterTimesheetsForCrew = useCallback(
    (timesheetRows: Timesheet[]) => timesheetRows.filter((row) => !workerLocked || allowedTeamIds.includes(row.teamId ?? '')),
    [allowedTeamIds, workerLocked]
  );

  const load = async () => {
    if (!activeProjectId) return;
    try {
      setLoading(true);
      setError(null);
      setNotice(null);
      const [cachedTimesheets, cachedTeamRows, cachedWorkerRows] = fieldMode
        ? await Promise.all([
          cachedRecords<Timesheet>('timesheet', activeProjectId),
          cachedTeams(),
          cachedWorkers(),
        ])
        : [[], [], []] as [Timesheet[], TeamListItem[], Worker[]];
      const scopedCachedTimesheets = filterTimesheetsForCrew(cachedTimesheets);
      const scopedCachedTeams = filterTeamsForCrew(cachedTeamRows);
      const scopedCachedWorkers = filterWorkersForCrew(cachedWorkerRows);
      if (fieldMode && !navigator.onLine) {
        setRows(filterTimesheetRows(scopedCachedTimesheets, status));
        setTeams(scopedCachedTeams);
        setWorkers(scopedCachedWorkers);
        return;
      }
      const teamId = workerLocked ? activeCrewId || undefined : undefined;
      const [timesheets, teamRows, workerRows] = await Promise.all([
        timesheetService.list({ projectId: activeProjectId, status, ...(teamId ? { teamId } : {}) }),
        workerLocked
          ? teamId
            ? teamService.getTeam(teamId).then((team) => [team])
            : Promise.resolve(scopedCachedTeams)
          : teamService.getTeams(),
        timesheetService.listWorkers({ active: true, ...(teamId ? { teamId } : {}) }),
      ]);
      setRows(fieldMode ? filterTimesheetRows(filterTimesheetsForCrew(mergeById(timesheets, scopedCachedTimesheets)), status) : timesheets);
      setTeams(filterTeamsForCrew(mergeById(teamRows, scopedCachedTeams)));
      setWorkers(filterWorkersForCrew(mergeById(workerRows, scopedCachedWorkers)));
    } catch (err: any) {
      if (fieldMode) {
        const [cachedTimesheets, cachedTeamRows, cachedWorkerRows] = await Promise.all([
          cachedRecords<Timesheet>('timesheet', activeProjectId),
          cachedTeams(),
          cachedWorkers(),
        ]);
        const scopedCachedTimesheets = filterTimesheetsForCrew(cachedTimesheets);
        const scopedCachedTeams = filterTeamsForCrew(cachedTeamRows);
        const scopedCachedWorkers = filterWorkersForCrew(cachedWorkerRows);
        setRows(filterTimesheetRows(scopedCachedTimesheets, status));
        setTeams(scopedCachedTeams);
        setWorkers(scopedCachedWorkers);
        setError(cachedTimesheets.length || cachedTeamRows.length ? null : err.response?.data?.error || err.response?.data?.message || 'Failed to load timesheets');
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load timesheets');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, status]);

  useEffect(() => {
    setForm((current) => ({ ...current, projectId: activeProjectId ?? '' }));
  }, [activeProjectId]);

  const totals = useMemo(() => ({
    sheets: rows.length,
    hours: rows.reduce((sum, row) => sum + (row.entries ?? []).reduce((entrySum, entry) => entrySum + Number(entry.hours ?? 0), 0), 0),
  }), [rows]);
  const selectedTaskIds = useMemo(
    () => (selected?.entries ?? []).map((entry) => entry.taskId).filter((id): id is string => Boolean(id)),
    [selected]
  );

  const openCreate = () => {
    const firstTeamId = teams[0]?.id ?? '';
    setForm({
      projectId: activeProjectId ?? '',
      workDate: today(),
      teamId: firstTeamId,
      entries: [newEntry()],
    });
    setDialogOpen(true);
  };

  const save = async (submitAfter = false) => {
    const payload = {
      ...form,
      entries: (form.entries ?? [])
        .filter((entry) => entry.workerId && Number(entry.hours) >= 0)
        .map((entry) => ({
          ...entry,
          taskId: entry.taskId || null,
          notes: entry.notes || null,
          hours: Number(entry.hours),
        })),
    };
    try {
      if (fieldMode && !navigator.onLine) {
        throw new Error('offline');
      }
      const created = await timesheetService.create(payload);
      if (submitAfter) await timesheetService.submit(created.id);
      setDialogOpen(false);
      await load();
    } catch (err: any) {
      if (fieldMode) {
        const local = await enqueueOfflineTimesheet(payload, submitAfter);
        setRows((current) => filterTimesheetRows(mergeById(current, [local]), status));
        setDialogOpen(false);
        setNotice('Timesheet saved offline and will sync when the device reconnects.');
        setError(null);
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save timesheet');
      }
    }
  };

  const approve = async (row: Timesheet) => {
    try {
      await timesheetService.approve(row.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to approve timesheet');
    }
  };

  const returnTimesheet = async (row: Timesheet) => {
    try {
      await timesheetService.return(row.id);
      setNotice('Timesheet returned for revision.');
      await load();
      setSelected(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to return timesheet');
    }
  };

  const createWorker = async () => {
    try {
      await timesheetService.createWorker({
        name: workerForm.name,
        badgeCode: workerForm.badgeCode || null,
        teamId: workerForm.teamId || null,
        trade: workerForm.trade || null,
      });
      setWorkerDialogOpen(false);
      setWorkerForm({ name: '', badgeCode: '', teamId: '', trade: '' });
      setWorkers(await timesheetService.listWorkers({ active: true, ...(activeCrewId ? { teamId: activeCrewId } : {}) }));
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create worker');
    }
  };

  const updateEntry = (index: number, patch: Partial<TimesheetEntryPayload>) => {
    const entries = [...(form.entries ?? [])];
    entries[index] = { ...entries[index], ...patch };
    setForm({ ...form, entries });
  };

  const handleBadgeScan = useCallback(async (code: string) => {
    const entryIndex = scannerEntryIndex;
    setScannerEntryIndex(null);
    if (entryIndex === null) return;

    try {
      const worker = await resolveWorkerByBadge(code, workers, { teamIds: allowedTeamIds });
      if (!worker) {
        setError(`No active worker found for badge ${code.trim()}${workerLocked ? ' in your crew' : ''}`);
        return;
      }

      setWorkers((current) => current.some((row) => row.id === worker.id) ? current : [...current, worker].sort((a, b) => a.name.localeCompare(b.name)));
      const entries = [...(form.entries ?? [])];
      entries[entryIndex] = { ...entries[entryIndex], workerId: worker.id };
      setForm({ ...form, entries });
      setNotice(`Badge ${code.trim()} matched ${worker.name}`);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to resolve scanned badge');
    }
  }, [allowedTeamIds, form, scannerEntryIndex, workerLocked, workers]);

  if (!activeProjectId) {
    return <Box><PageHeader title="Timesheets" /><Alert severity="info">Select a project to view timesheets.</Alert></Box>;
  }

  return (
    <Box>
      <PageHeader
        title="Timesheets"
        subtitle={`${totals.sheets} sheets / ${totals.hours.toFixed(2)} hours`}
        actions={
          <Stack direction="row" spacing={1}>
            <IconButton onClick={load} disabled={loading} aria-label="Refresh timesheets"><Refresh /></IconButton>
            {canManageWorkers && <Button variant="outlined" startIcon={<Add />} onClick={() => setWorkerDialogOpen(true)}>Worker</Button>}
            {canCreateTimesheet && <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Timesheet</Button>}
          </Stack>
        }
      />
      {workerLocked && !activeCrewId && (
        <Alert severity="warning" sx={{ mb: 2 }}>This worker account is not assigned to a crew.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <TextField select size="small" label="Status" value={status} onChange={(event) => setStatus(event.target.value as TimesheetStatus | '')} sx={{ minWidth: 180 }}>
          <MenuItem value="">All</MenuItem>
          {(['draft', 'submitted', 'approved', 'returned'] as TimesheetStatus[]).map((value) => <MenuItem key={value} value={value}>{labelize(value)}</MenuItem>)}
        </TextField>
      </Stack>
      <Paper sx={{ overflow: 'hidden', borderRadius: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Team</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell>Submitted</TableCell>
              {!fieldMode && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const hours = (row.entries ?? []).reduce((sum, entry) => sum + Number(entry.hours ?? 0), 0);
              return (
                <TableRow key={row.id}>
                  <TableCell>{row.workDate}</TableCell>
                  <TableCell>{teams.find((team) => team.id === row.teamId)?.name ?? row.teamId ?? '-'}</TableCell>
                  <TableCell><Chip size="small" color={row.status === 'approved' ? 'success' : row.status === 'submitted' ? 'primary' : 'default'} label={labelize(row.status)} /></TableCell>
                  <TableCell>{hours.toFixed(2)}</TableCell>
                  <TableCell>{row.submittedAt ? 'Yes' : 'No'}</TableCell>
                  {!fieldMode && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton aria-label="View timesheet" onClick={() => setSelected(row)}><Visibility /></IconButton>
                        {canApprove && row.status === 'submitted' && (
                          <>
                            <IconButton color="success" aria-label="Approve timesheet" onClick={() => approve(row)}><CheckCircle /></IconButton>
                            <IconButton color="warning" aria-label="Return timesheet" onClick={() => returnTimesheet(row)}><Reply /></IconButton>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth fullScreen={fieldMode}>
        <DialogTitle>New Timesheet</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Work Date" InputLabelProps={{ shrink: true }} value={form.workDate} onChange={(event) => setForm({ ...form, workDate: event.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth select label="Team" value={form.teamId} onChange={(event) => setForm({ ...form, teamId: event.target.value })}>{teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>)}</TextField></Grid>
            {(form.entries ?? []).map((entry, index) => (
              <Grid item xs={12} key={index}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField fullWidth select label="Worker" value={entry.workerId} onChange={(event) => updateEntry(index, { workerId: event.target.value })}>{workers.map((worker) => <MenuItem key={worker.id} value={worker.id}>{worker.name}</MenuItem>)}</TextField>
                        {fieldMode && (
                          <Button
                            variant="outlined"
                            startIcon={<QrCodeScanner />}
                            onClick={() => setScannerEntryIndex(index)}
                            sx={{ minWidth: { xs: '100%', sm: 132 } }}
                          >
                            Scan Badge
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={2}><TextField fullWidth type="number" label="Hours" value={entry.hours} onChange={(event) => updateEntry(index, { hours: Number(event.target.value) })} /></Grid>
                    <Grid item xs={12} sm={3}><TextField fullWidth select label="Type" value={entry.hourType} onChange={(event) => updateEntry(index, { hourType: event.target.value as TimesheetHourType })}><MenuItem value="regular">Regular</MenuItem><MenuItem value="overtime">Overtime</MenuItem></TextField></Grid>
                    <Grid item xs={12} sm={3}><TextField fullWidth label="Task ID" value={entry.taskId ?? ''} onChange={(event) => updateEntry(index, { taskId: event.target.value })} /></Grid>
                    <Grid item xs={12}><TextField fullWidth label="Notes" value={entry.notes ?? ''} onChange={(event) => updateEntry(index, { notes: event.target.value })} /></Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
            <Grid item xs={12}><Button onClick={() => setForm({ ...form, entries: [...(form.entries ?? []), newEntry()] })}>Add Row</Button></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => save(false)} disabled={!form.projectId || !form.workDate || !form.teamId}>Save Draft</Button>
          <Button variant="contained" startIcon={<Send />} onClick={() => save(true)} disabled={!form.projectId || !form.workDate || !form.teamId}>Submit</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={workerDialogOpen} onClose={() => setWorkerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Worker</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField label="Name" value={workerForm.name} onChange={(event) => setWorkerForm({ ...workerForm, name: event.target.value })} />
            <TextField label="Badge Code" value={workerForm.badgeCode} onChange={(event) => setWorkerForm({ ...workerForm, badgeCode: event.target.value })} />
            <TextField select label="Team" value={workerForm.teamId} onChange={(event) => setWorkerForm({ ...workerForm, teamId: event.target.value })}><MenuItem value="">None</MenuItem>{teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>)}</TextField>
            <TextField label="Trade" value={workerForm.trade} onChange={(event) => setWorkerForm({ ...workerForm, trade: event.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createWorker} disabled={!workerForm.name.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      <BarcodeScanner
        open={scannerEntryIndex !== null}
        title="Scan Worker Badge"
        onClose={() => setScannerEntryIndex(null)}
        onScan={handleBadgeScan}
      />

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selected?.workDate} Timesheet</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Worker</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(selected?.entries ?? []).map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.worker?.name ?? entry.workerId}</TableCell>
                    <TableCell>{Number(entry.hours).toFixed(2)}</TableCell>
                    <TableCell>{labelize(entry.hourType)}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800 }}>{entry.task?.name ?? entry.taskId ?? '-'}</Typography>
                      {entry.task?.taskCode && <Typography variant="caption" color="text.secondary">{entry.task.taskCode}</Typography>}
                    </TableCell>
                    <TableCell>{entry.notes ?? '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TaskPhotoEvidencePanel
              projectId={selected?.projectId}
              taskIds={selectedTaskIds}
              title="Photos linked to this timesheet's tasks"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {!fieldMode && selected?.status === 'submitted' && canApprove && (
            <>
              <Button color="warning" startIcon={<Reply />} onClick={() => selected && returnTimesheet(selected)}>Return</Button>
              <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => selected && approve(selected)}>Approve</Button>
            </>
          )}
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function filterTimesheetRows(rows: Timesheet[], status: TimesheetStatus | ''): Timesheet[] {
  return rows
    .filter((row) => !status || row.status === status)
    .sort((a, b) => `${b.workDate}${b.createdAt}`.localeCompare(`${a.workDate}${a.createdAt}`));
}
